import { Card } from "@/components/Card";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "@/components/Select";
import { applications, AppId } from "./Applications";
import IntegrationsModal from "./NetSuite/NetsuitePreviewModal";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import useApi from "@/hooks/useApi";
import { useIntegrationConfigsCache } from "@/hooks/useIntegrationConfigsCache";
import {
  fetchNetSuiteConfig,
  fetchFormstackConfig,
  fetchAccelaConfig,
  fetchShopifyConfig,
  fetchHubSpotConfig,
  fetchBigCommerceConfig,
  fetchDynamicsConfig,
  fetchWooCommerceConfig,
  fetchMagentoConfig,
  fetchSalesforceConfig,
  fetchServiceNowConfig
} from "./IntegrationConfigs";
import { appCategories } from "./Categories";
import { IconProps } from "@velaro/velaro-component-lib";
import IntegrationsNewApp from "./IntegrationsNewApp";
import useAdminFeatures from "@/hooks/useAdminFeatures";
import UnlockIntegrationPanel from "./shared/UnlockIntegrationPanel";

interface NetSuiteConfig {
  id: number;
  displayName: string;
  company: string;
  teamId: number;
  createdAt: string;
}

interface FormstackConfig {
  displayName: string;
  token: string;
}

interface AccelaConfig {
  id: number;
  agencyName: string;
  clientId: string;
  clientSecret: string;
  emailAddress: string;
  accessToken: string;
  refreshToken: string;
  scope?: string;
  teamId: number;
  token?: string;
  refreshTokenExpired?: boolean;
}

interface ShopifyConfig {
  id: number;
  displayName: string;
  company: string;
  teamId: number;
  createdAt: string;
}

type App = {
  id: string;
  name: string;
  category: string;
  icon?: string | ((props: IconProps) => JSX.Element); // Accept both image paths and components
  subTitle: string;
};

interface OptionType {
  value: string | number;
  label: string;
}

const customSingleValue = ({ data }: { data: OptionType }) => (
  <div className="flex items-center gap-2">
    <img src="" alt="Selected" className="w-4 h-4" />
    {data.label}
  </div>
);

const customOption = (props: {
  data: any;
  isSelected: any;
  innerRef: any;
  innerProps: any;
}) => {
  const { data, isSelected, innerRef, innerProps } = props;
  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={`px-3 py-2 cursor-pointer flex items-center gap-2 ${
        isSelected ? "bg-blue-100" : "bg-white"
      }`}
    >
      {isSelected && <img src="" alt="Selected" className="w-4 h-4" />}
      {data.label}
    </div>
  );
};
interface DetectionSuggestion {
  platform: string;
  platformLabel: string;
  integrationId: string;
  integrationName: string;
  reason: string;
  integrationUrl: string;
  dismissed: boolean;
}

export default function Landing() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [visibleCount, setVisibleCount] = useState(12);
  // statusesReady: false until all connection statuses have been fetched once.
  // Prevents card shuffle while async status calls complete.
  const CACHE_KEY = "velaro-integration-statuses";
  const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  const [statusesReady, setStatusesReady] = useState<boolean>(() => {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return false;
      const { savedAt } = JSON.parse(raw) as { savedAt: number };
      return Date.now() - savedAt < CACHE_TTL;
    } catch (ex) { console.error("Integration cache read error", ex); return false; }
  });
  const [selectedApp, setSelectedApp] = useState<App | null>(null);
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false);
  const [suggestions, setSuggestions] = useState<DetectionSuggestion[]>([]);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  const [netSuiteConfigs, setNetSuiteConfigs] = useState<NetSuiteConfig[]>([]);
  const [formstackConfig, setFormstackConfig] =
    useState<FormstackConfig | null>(null);
  const [accelaConfigs, setAccelaConfigs] = useState<AccelaConfig | null>(null);
  const [shopifyConfigs, setShopifyConfigs] = useState<ShopifyConfig | null>(null);
  const [hubSpotConfigs, setHubSpotConfigs] = useState<any[] | null>(null);
  const [bigCommerceConfigs, setBigCommerceConfigs] = useState<any[] | null>(null);
  const [dynamicsConfigs, setDynamicsConfigs] = useState<any[] | null>(null);
  const [wooCommerceConfigs, setWooCommerceConfigs] = useState<any[] | null>(null);
  const [magentoConfigs, setMagentoConfigs] = useState<any[] | null>(null);
  const [salesforceConfigs, setSalesforceConfigs] = useState<any[] | null>(null);
  const [serviceNowConfigs, setServiceNowConfigs] = useState<any[] | null>(null);
  const [uplistingConfigs, setUplistingConfigs] = useState<any[] | null>(null);
  const [guestyConfigs, setGuestyConfigs] = useState<any[] | null>(null);
  const [cloudbedsConfigs, setCloudbedsConfigs] = useState<any[] | null>(null);
  const [pipedriveConfigs, setPipedriveConfigs] = useState<any[] | null>(null);
  const [quickBooksConfigs, setQuickBooksConfigs] = useState<any[] | null>(null);
  const [zohoConfigs, setZohoConfigs] = useState<any[] | null>(null);
  const [toastConfigs, setToastConfigs] = useState<any[] | null>(null);
  const [cloverConfigs, setCloverConfigs] = useState<any[] | null>(null);
  const [openTableConfig, setOpenTableConfig] = useState<any | null>(null);
  const [resyConfig, setResyConfig] = useState<any | null>(null);
  const [mindbodyConfigs, setMindbodyConfigs] = useState<any[] | null>(null);
  const [jobberConfigs, setJobberConfigs] = useState<any[] | null>(null);
  const {
    enableMcp, enableHubSpot, enableBigCommerce, enableDynamics,
    enableTeamsNotifications, enableSlackNotifications, enableOperationalAlerts, enableMagento,
    enableCalendarBooking, enableShopify,
    enableWooCommerce, enableUplisting, enableGuesty, enableCloudbeds,
    enableNativeCrm, enablePipedrive, enableQuickBooks, enableZoho,
    enableToast, enableMindbody, enableJobber,
    enableNetsuite, enableSalesforce, enableKlaviyo, enableAccela,
    enableFormstack, enableSquare, enableClover, enablePowerAutomate,
    enableMake, enableZapier, enableTranscriptStorage, enableServiceNow,
    enableRecruitingBot, enableMicrosoftBookings, enableSecureForms,
    enableVast, enableNextiva, enableGmailInbox, enableOutlookInbox,
    enableSapSkills,
    enableOpenTableSkills, enableResySkills
  } = useAdminFeatures();
  const api = useApi();
  const { teamId } = useSelectedTeam();
  const [appStatus, setAppStatuses] = useState<{ [key: string]: string }>(() => {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return {};
      const { statuses, savedAt } = JSON.parse(raw) as { statuses: Record<string, string>; savedAt: number };
      return Date.now() - savedAt < CACHE_TTL ? statuses : {};
    } catch (ex) { console.error("Integration cache read error", ex); return {}; }
  });
  const [showNewAppPanel, setShowNewAppPanel] = useState(false);
  /**
   * Tracks which integration triggered the unlock panel (null = panel closed).
   * appId is used to look up marketplace links; appName pre-fills the contact form.
   */
  const [upgradeTarget, setUpgradeTarget] = useState<{ appId: string; appName: string } | null>(null);

  // ─────────────────────────────────────────────────────────────────────────
  // INTEGRATION GATE MAP
  // ─────────────────────────────────────────────────────────────────────────
  // Maps each integration app ID to a boolean flag value:
  //   true  → accessible (subscription includes this integration)
  //   false → locked (show lock badge + "Upgrade" CTA, no navigation)
  //   undefined → no gate (always accessible, e.g. free-tier or no flag yet)
  //
  // HOW TO ADD A NEW INTEGRATION:
  //   1. Add your app to Applications.ts with a unique id.
  //   2. Add the subscription or admin feature flag to useAdminFeatures / useSubscriptions.
  //   3. Add one entry here: "your-app-id": adminFeatures.enableYourApp
  //   4. That's it — Card, sort order, click handling, and UnlockIntegrationPanel
  //      (with marketplace + direct paths) are all wired automatically.
  //
  // FLAG SOURCES:
  //   subscription?.enableX  — v10 subscription table (billing-gated)
  //   adminEnableX           — useAdminFeatures (v20 admin features endpoint)
  //   undefined              — no gate (integration is always visible & clickable)
  // ─────────────────────────────────────────────────────────────────────────
  // Record<AppId, ...> enforces at compile time that every app in Applications.ts
  // has an entry here. Missing an entry = tsc error. That IS the guard.
  // ─────────────────────────────────────────────────────────────────────────
  // INTEGRATION GATE MAP — all flags come from useAdminFeatures (GET /Features)
  // ─────────────────────────────────────────────────────────────────────────
  // Maps each integration app ID to a boolean flag value:
  //   true  → accessible (subscription includes this integration)
  //   false → locked (show lock badge + "Upgrade" CTA, no navigation)
  //   undefined → no gate (always accessible, e.g. free-tier or no flag yet)
  //
  // HOW TO ADD A NEW INTEGRATION:
  //   1. Add your app to Applications.ts with a unique id.
  //   2. Add the flag to FeaturesController.cs + useAdminFeatures.ts.
  //   3. Add one entry here: "your-app-id": enableYourApp
  //   4. That's it — Card, sort order, click handling, and UnlockIntegrationPanel
  //      are all wired automatically.
  //
  // Record<AppId, ...> enforces at compile time that every app in Applications.ts
  // has an entry here. Missing an entry = tsc error. That IS the guard.
  // Cache integration config fetches to avoid redundant API calls when navigating back
  const configCache = useIntegrationConfigsCache();

  // Invalidate cache when subscription features change — ensures new features are accessible immediately
  useEffect(() => {
    configCache.invalidateAll();
  }, [
    enableHubSpot, enableBigCommerce, enableDynamics, enableMagento,
    enableShopify, enableWooCommerce, enableNetsuite, enableSalesforce,
    enableUplisting, enableGuesty, enableCloudbeds, enableSquare,
    configCache
  ]);

  const integrationGateMap: Record<AppId, boolean | undefined> = {
    // ── CRM ──────────────────────────────────────────────────────────────
    "velaro-crm":          enableNativeCrm,
    "netsuite":            enableNetsuite,
    "hubspot":             enableHubSpot,
    "salesforce":          enableSalesforce,
    "dynamics":            enableDynamics,
    "pipedrive":           enablePipedrive,
    "zoho":                enableZoho,

    // ── E-commerce ───────────────────────────────────────────────────────
    "shopify":             enableShopify,
    "bigcommerce":         enableBigCommerce,
    "woocommerce":         enableWooCommerce,
    "magento":             enableMagento,
    "klaviyo":             enableKlaviyo,
    "stripe-payments":     undefined,              // payment processing, no gate

    // ── Government / Vertical ────────────────────────────────────────────
    "accela-civic":        enableAccela,
    "formstack":           enableFormstack,

    // ── Hospitality ──────────────────────────────────────────────────────
    "uplisting":           enableUplisting,
    "guesty":              enableGuesty,
    "cloudbeds":           enableCloudbeds,

    // ── POS / Booking ────────────────────────────────────────────────────
    "square-appointments": enableSquare,
    "toast":               enableToast,
    "clover":              enableClover,
    "opentable":           enableOpenTableSkills,
    "resy":                enableResySkills,
    "mindbody":            enableMindbody,
    "quickbooks":          enableQuickBooks,

    // ── Scheduling ───────────────────────────────────────────────────────
    "outlook-calendar":    enableCalendarBooking,
    "calendly":            enableCalendarBooking,

    // ── Notifications ────────────────────────────────────────────────────
    "teams-notifications": enableTeamsNotifications,
    "slack-notifications": enableSlackNotifications,
    "alert-rules":         enableOperationalAlerts,

    // ── Automation / Webhooks ────────────────────────────────────────────
    "power-automate":      enablePowerAutomate,
    "make":                enableMake,
    "zapier":              enableZapier,

    // ── Platform / Infrastructure ─────────────────────────────────────────
    "mcp":                 undefined,              // always accessible
    "microsoft-365":       undefined,              // future: enableMicrosoft365 flag
    "google-tag-manager":  undefined,              // free feature, no gate
    "transcript-storage":  enableTranscriptStorage,
    "servicenow":          enableServiceNow,

    // ── Migration Tools ──────────────────────────────────────────────────
    "gorgias":             undefined,              // migration helper, always accessible

    // ── HR / Recruiting ──────────────────────────────────────────────────
    "recruiting":          enableRecruitingBot,

    // ── Field Service ────────────────────────────────────────────────────
    "jobber":              enableJobber,

    // ── Microsoft Bookings ────────────────────────────────────────────────
    "microsoft-bookings":  enableMicrosoftBookings,

    // ── SecureForms ────────────────────────────────────────────────────────
    "secure-forms":        enableSecureForms,

    // ── Auto Service ────────────────────────────────────────────────────────
    "vast":                enableVast,

    // ── VoIP ────────────────────────────────────────────────────────────────
    "nextiva":             enableNextiva,

    // ── Email Inbox ───────────────────────────────────────────────────────────
    "gmail-inbox":         enableGmailInbox,
    "outlook-inbox":       enableOutlookInbox,

    // ── ERP ──────────────────────────────────────────────────────────────────
    "sap":                 enableSapSkills,
  };

  /** Returns true if the integration is locked for this subscription. */
  const isLocked = (appId: string): boolean => integrationGateMap[appId] === false;

  useEffect(() => {
    const fetchStatuses = async () => {
      const newStatuses: { [key: string]: string } = {};

      if (netSuiteConfigs.length > 0) {
        newStatuses["netsuite"] = await checkIfMappingExists("netsuite");
      }

      if (formstackConfig != null) {
        newStatuses["formstack"] = "Connected";
      }

      if (accelaConfigs != null) {
        newStatuses["accela-civic"] = "Connected";
      }

      if (shopifyConfigs != null) {
        newStatuses["shopify"] = "Connected";
      }

      if (hubSpotConfigs != null && hubSpotConfigs.length > 0) {
        newStatuses["hubspot"] = "Connected";
      }

      if (bigCommerceConfigs != null && bigCommerceConfigs.length > 0) {
        newStatuses["bigcommerce"] = "Connected";
      }

      if (magentoConfigs != null && magentoConfigs.length > 0) {
        newStatuses["magento"] = "Connected";
      }

      if (salesforceConfigs != null && salesforceConfigs.length > 0) {
        newStatuses["salesforce"] = "Connected";
      }

      if (serviceNowConfigs != null && serviceNowConfigs.length > 0) {
        newStatuses["servicenow"] = "Connected";
      }

      if (dynamicsConfigs != null && dynamicsConfigs.length > 0) {
        newStatuses["dynamics"] = "Connected";
      }

      if (uplistingConfigs != null && uplistingConfigs.length > 0) {
        newStatuses["uplisting"] = "Connected";
      }

      if (guestyConfigs != null && guestyConfigs.length > 0) {
        newStatuses["guesty"] = "Connected";
      }

      if (cloudbedsConfigs != null && cloudbedsConfigs.length > 0) {
        newStatuses["cloudbeds"] = "Connected";
      }

      if (pipedriveConfigs != null && pipedriveConfigs.length > 0) {
        newStatuses["pipedrive"] = "Connected";
      }

      if (quickBooksConfigs != null && quickBooksConfigs.length > 0) {
        newStatuses["quickbooks"] = "Connected";
      }

      if (zohoConfigs != null && zohoConfigs.length > 0) {
        newStatuses["zoho"] = "Connected";
      }

      if (toastConfigs != null && toastConfigs.length > 0) {
        newStatuses["toast"] = "Connected";
      }

      if (cloverConfigs != null && cloverConfigs.length > 0) {
        newStatuses["clover"] = "Connected";
      }

      if (openTableConfig?.hasApiKey) {
        newStatuses["opentable"] = "Connected";
      }

      if (resyConfig?.hasApiKey) {
        newStatuses["resy"] = "Connected";
      }

      if (mindbodyConfigs != null && mindbodyConfigs.length > 0) {
        newStatuses["mindbody"] = "Connected";
      }

      if (jobberConfigs != null && jobberConfigs.length > 0) {
        newStatuses["jobber"] = "Connected";
      }

      try {
        const mcpResp = await api.admin.get("McpServers");
        if (mcpResp.ok) {
          const mcpData = await mcpResp.json();
          if (Array.isArray(mcpData) && mcpData.some((s: { enabled: boolean }) => s.enabled)) {
            newStatuses["mcp"] = "Connected";
          }
        }
      } catch {
        // non-critical — MCP badge just won't show
      }

      try {
        const teamsRes = await api.admin.get("TeamsNotification");
        const teamsData = await teamsRes.json();
        if (Array.isArray(teamsData) && teamsData.some((c: { isEnabled: boolean }) => c.isEnabled)) {
          newStatuses["teams-notifications"] = "Connected";
        }
      } catch { /* non-critical */ }

      try {
        const slackRes = await api.admin.get("SlackNotification");
        const slackData = await slackRes.json();
        if (Array.isArray(slackData) && slackData.some((c: { isEnabled: boolean }) => c.isEnabled)) {
          newStatuses["slack-notifications"] = "Connected";
        }
      } catch { /* non-critical */ }

      try {
        const calRes  = await api.admin.get("OutlookCalendar/Status");
        const calData = await calRes.json();
        if (calData?.configured) newStatuses["outlook-calendar"] = "Connected";
      } catch { /* non-critical */ }

      try {
        const bookingsRes  = await api.admin.get("MicrosoftBookings/Status");
        const bookingsData = await bookingsRes.json();
        if (bookingsData?.connected) newStatuses["microsoft-bookings"] = "Connected";
      } catch { /* non-critical */ }

      try {
        const clyRes  = await api.admin.get("Calendly/Status");
        const clyData = await clyRes.json();
        if (clyData?.configured) newStatuses["calendly"] = "Connected";
      } catch { /* non-critical */ }

      try {
        const sqRes  = await api.admin.get("Square/Status");
        const sqData = await sqRes.json();
        if (sqData?.configured) newStatuses["square-appointments"] = "Connected";
      } catch { /* non-critical */ }

      try {
        const klavRes  = await api.admin.get("Klaviyo/Status");
        const klavData = await klavRes.json();
        if (klavData?.connected) newStatuses["klaviyo"] = "Connected";
      } catch { /* non-critical */ }

      try {
        const stripeRes  = await api.admin.get("Stripe/Config");
        const stripeData = await stripeRes.json();
        if (stripeData?.configured) newStatuses["stripe-payments"] = "Connected";
      } catch { /* non-critical */ }

      setAppStatuses(newStatuses);
      setStatusesReady(true);
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ statuses: newStatuses, savedAt: Date.now() }));
      } catch { /* sessionStorage unavailable — non-critical */ }
    };

    fetchStatuses();
  }, [netSuiteConfigs, formstackConfig, accelaConfigs, shopifyConfigs, hubSpotConfigs, bigCommerceConfigs, magentoConfigs, dynamicsConfigs, salesforceConfigs, serviceNowConfigs, uplistingConfigs, guestyConfigs, cloudbedsConfigs, pipedriveConfigs, quickBooksConfigs, zohoConfigs, toastConfigs, mindbodyConfigs, cloverConfigs, jobberConfigs, openTableConfig, resyConfig, api.admin]);

  const [configId, setConfigId] = useState<string | "">("");
  const checkIfMappingExists = async (appId: string): Promise<string> => {
    if (appId === "netsuite" && netSuiteConfigs.length > 0) {
      const latestConfigId =
        [...netSuiteConfigs].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0]?.id || "";

      setConfigId(latestConfigId ? String(latestConfigId) : "");

      try {
        const resp = await api.messaging.get(
          `NetSuite/Integrations/Mapping/${latestConfigId}`
        );
        const count = await resp.json();

        return count > 0 ? "Connected" : "Pending";
      } catch (error) {
        console.error("Error fetching NetSuite config:", error);
        return "Error";
      }
    }

    return "";
  };
  const filteredApps = (() => {
    let apps =
      selectedCategory === "all"
        ? applications
        : selectedCategory === "my-apps"
        ? applications.filter((app) => app.id !== "new" && !!appStatus[app.id])
        : applications.filter((app) => app.category === selectedCategory);

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      apps = apps.filter(
        (app) =>
          app.id === "new" ||
          app.name.toLowerCase().includes(q) ||
          app.subTitle?.toLowerCase().includes(q) ||
          app.category?.toLowerCase().includes(q)
      );
    }
    return apps;
  })();

  const showApps = filteredApps.sort((a, b) => {
    // "Request New App" card is always pinned first
    if (a.id === "new") return -1;
    if (b.id === "new") return 1;

    // Sort tier: 0 = connected, 1 = unlocked (not yet connected), 2 = locked
    const tier = (app: { id: string }) => {
      if (appStatus[app.id]) return 0;   // connected
      if (isLocked(app.id)) return 2;    // locked (subscription gate)
      return 1;                          // unlocked, not yet connected
    };

    const diff = tier(a) - tier(b);
    if (diff !== 0) return diff;
    return a.name.localeCompare(b.name); // alphabetical within each tier
  });

  React.useEffect(() => {
    setVisibleCount(12);
  }, [selectedCategory]);

  const handleLoadMore = () => {
    setVisibleCount((prevCount) => prevCount + 6);
  };

  const handleCardClick = async (app: App) => {
    if (app.id === "new") {
      setShowNewAppPanel(true);
      return;
    }

    // Locked integrations open the unlock panel instead of navigating.
    // This is driven automatically by integrationGateMap — no per-integration code needed.
    // The panel shows marketplace links (if any) AND the direct Velaro contact form.
    if (isLocked(app.id)) {
      setUpgradeTarget({ appId: app.id, appName: app.name });
      return;
    }

    const hasConfig = checkIfConfigExists(app);
    if (hasConfig) {
      navigate(`/integrations/${app.id}`);
    } else {
      if (app.id === "formstack") {
        navigate(`/integrations/${app.id}/${app.id}create`);
      } else if (app.id === "accela-civic") {
        navigate(`/integrations/accela-civic/AccelaCreate`);
      } else if (app.id === "shopify") {
        navigate(`/integrations/shopify/ShopifyCreate`);
      } else if (app.id === "netsuite") {
        navigate(`/integrations/netsuite/netsuitecreate`);
      } else if (app.id === "hubspot") {
        navigate(`/integrations/hubspot/overview`);
      } else if (app.id === "bigcommerce") {
        navigate(`/integrations/bigcommerce/overview`);
      } else if (app.id === "magento") {
        navigate(`/integrations/magento/overview`);
      } else if (app.id === "dynamics") {
        navigate(`/integrations/dynamics/overview`);
      } else if (app.id === "mcp") {
        navigate(`/integrations/mcp`);
      } else if (app.id === "teams-notifications") {
        navigate(`/integrations/teams-notifications`);
      } else if (app.id === "slack-notifications") {
        navigate(`/integrations/slack-notifications`);
      } else if (app.id === "alert-rules") {
        navigate(`/integrations/alert-rules`);
      } else if (app.id === "outlook-calendar") {
        navigate(`/integrations/outlook-calendar`);
      } else if (app.id === "calendly") {
        navigate(`/integrations/calendly`);
      } else if (app.id === "square-appointments") {
        navigate(`/integrations/square-appointments`);
      } else if (app.id === "power-automate") {
        navigate(`/integrations/power-automate`);
      } else if (app.id === "make") {
        navigate(`/integrations/make`);
      } else if (app.id === "zapier") {
        navigate(`/integrations/zapier`);
      } else if (app.id === "woocommerce") {
        navigate(`/integrations/woocommerce`);
      } else if (app.id === "stripe-payments") {
        navigate(`/integrations/payments/stripe`);
      } else if (app.id === "klaviyo") {
        navigate(`/integrations/klaviyo`);
      } else if (app.id === "velaro-crm") {
        navigate(`/integrations/velaro-crm`);
      } else if (app.id === "salesforce") {
        navigate(`/integrations/salesforce`);
      } else if (app.id === "gorgias") {
        navigate(`/integrations/gorgias`);
      } else if (app.id === "google-tag-manager") {
        navigate(`/integrations/google-tag-manager`);
      } else if (app.id === "servicenow") {
        navigate(`/integrations/servicenow`);
      } else if (app.id === "uplisting") {
        navigate(`/integrations/uplisting`);
      } else if (app.id === "guesty") {
        navigate(`/integrations/guesty`);
      } else if (app.id === "cloudbeds") {
        navigate(`/integrations/cloudbeds`);
      } else if (app.id === "pipedrive") {
        navigate(`/integrations/pipedrive`);
      } else if (app.id === "quickbooks") {
        navigate(`/integrations/quickbooks`);
      } else if (app.id === "zoho") {
        navigate(`/integrations/zoho`);
      } else if (app.id === "toast") {
        navigate(`/integrations/toast`);
      } else if (app.id === "mindbody") {
        navigate(`/integrations/mindbody`);
      } else if (app.id === "jobber") {
        navigate(`/integrations/jobber`);
      } else if (app.id === "microsoft-365") {
        navigate(`/integrations/microsoft-365`);
      } else if (app.id === "microsoft-bookings") {
        navigate(`/integrations/microsoft-bookings`);
      } else if (app.id === "opentable") {
        navigate(`/integrations/opentable`);
      } else if (app.id === "resy") {
        navigate(`/integrations/resy`);
      } else if (app.id === "secure-forms") {
        navigate(`/integrations/secure-forms`);
      } else if (app.id === "transcript-storage") {
        navigate(`/integrations/transcript-storage`);
      } else if (app.id === "gmail-inbox") {
        navigate(`/integrations/gmail-inbox`);
      } else if (app.id === "outlook-inbox") {
        navigate(`/integrations/outlook-inbox`);
      } else {
        setSelectedApp(app);
        setShowIntegrationsModal(true);
      }
    }
  };

  const checkIfConfigExists = (app: App): boolean => {
    if (app.id === "netsuite") {
      return netSuiteConfigs.length > 0;
    } else if (app.id === "formstack") {
      return formstackConfig !== null;
    } else if (app.id === "accela-civic") {
      return accelaConfigs !== null;
    } else if (app.id === "shopify") {
      return shopifyConfigs !== null;
    } else if (app.id === "hubspot") {
      return hubSpotConfigs !== null && hubSpotConfigs.length > 0;
    } else if (app.id === "bigcommerce") {
      return bigCommerceConfigs !== null && bigCommerceConfigs.length > 0;
    } else if (app.id === "magento") {
      return magentoConfigs !== null && magentoConfigs.length > 0;
    } else if (app.id === "dynamics") {
      return dynamicsConfigs !== null && dynamicsConfigs.length > 0;
    } else if (app.id === "mcp") {
      // Always navigate directly — MCP has its own management page
      return true;
    } else if (app.id === "teams-notifications" || app.id === "slack-notifications" || app.id === "alert-rules") {
      return true;
    } else if (app.id === "woocommerce") {
      // Always navigate directly — WooCommerce has its own management page
      return true;
    } else if (app.id === "stripe-payments") {
      return true;
    } else if (app.id === "klaviyo") {
      // Always navigate directly — Klaviyo has its own management page
      return true;
    } else if (app.id === "gorgias") {
      // Always navigate directly — Gorgias wizard has its own page
      return true;
    } else if (app.id === "salesforce") {
      // Always navigate directly — Salesforce has its own management page
      return true;
    } else if (app.id === "google-tag-manager") {
      // Always navigate directly — GTM has its own page
      return true;
    } else if (app.id === "servicenow") {
      return true;
    } else if (app.id === "uplisting") {
      return uplistingConfigs != null && uplistingConfigs.length > 0;
    } else if (app.id === "guesty") {
      return guestyConfigs != null && guestyConfigs.length > 0;
    } else if (app.id === "cloudbeds") {
      return cloudbedsConfigs != null && cloudbedsConfigs.length > 0;
    } else if (app.id === "pipedrive") {
      return pipedriveConfigs != null && pipedriveConfigs.length > 0;
    } else if (app.id === "quickbooks") {
      return quickBooksConfigs != null && quickBooksConfigs.length > 0;
    } else if (app.id === "zoho") {
      return zohoConfigs != null && zohoConfigs.length > 0;
    } else if (app.id === "toast") {
      return toastConfigs != null && toastConfigs.length > 0;
    } else if (app.id === "mindbody") {
      return mindbodyConfigs != null && mindbodyConfigs.length > 0;
    } else if (app.id === "jobber") {
      return jobberConfigs != null && jobberConfigs.length > 0;
    } else if (app.id === "microsoft-365") {
      return true;
    } else if (app.id === "microsoft-bookings") {
      return true;
    } else if (app.id === "transcript-storage") {
      return true;
    } else if (app.id === "square-appointments") {
      return true;
    } else if (app.id === "opentable") {
      return true;
    } else if (app.id === "resy") {
      return true;
    } else if (app.id === "outlook-calendar" || app.id === "calendly" || app.id === "power-automate" || app.id === "make" || app.id === "zapier") {
      // These always navigate to their own page regardless of config state
      return true;
    }
    return false;
  };

  React.useEffect(() => {
    async function loadConfigs() {
      if (teamId) {
        const netSuiteData = await configCache.fetch("netsuite", () => fetchNetSuiteConfig(api, teamId));
        if (netSuiteData) setNetSuiteConfigs(netSuiteData);
      }
      const formstackData = await configCache.fetch("formstack", () => fetchFormstackConfig(api, enableFormstack));
      if (formstackData) setFormstackConfig(formstackData);

      const accelaData = await configCache.fetch("accela", () => fetchAccelaConfig(api, enableAccela));
      if (accelaData) setAccelaConfigs(accelaData);

      const shopifyData = await configCache.fetch("shopify", () => fetchShopifyConfig(api, enableShopify));
      if (shopifyData) setShopifyConfigs(shopifyData);

      // Skip fetching configs for locked integrations — avoids 403s and wasted requests.
      // The flag checks below mirror integrationGateMap; skipping === false avoids the fetch.
      const hubSpotData = await configCache.fetch("hubspot", () => fetchHubSpotConfig(api, enableHubSpot));
      if (hubSpotData) setHubSpotConfigs(hubSpotData);

      const bigCommerceData = await configCache.fetch("bigcommerce", () => fetchBigCommerceConfig(api, enableBigCommerce));
      if (bigCommerceData) setBigCommerceConfigs(bigCommerceData);

      const dynamicsData = await configCache.fetch("dynamics", () => fetchDynamicsConfig(api, enableDynamics));
      if (dynamicsData) setDynamicsConfigs(dynamicsData);

      const magentoData = await configCache.fetch("magento", () => fetchMagentoConfig(api, enableMagento));
      if (magentoData) setMagentoConfigs(magentoData);

      // Salesforce / ServiceNow have no subscription gate yet — fetch always
      const salesforceData = await configCache.fetch("salesforce", () => fetchSalesforceConfig(api));
      if (salesforceData) setSalesforceConfigs(salesforceData);

      const serviceNowData = await configCache.fetch("servicenow", () => fetchServiceNowConfig(api));
      if (serviceNowData) setServiceNowConfigs(serviceNowData);

      if (enableUplisting !== false) {
        try {
          const uplistingData = await configCache.fetch("uplisting", () =>
            api.admin.get("Uplisting/Configs").then(r => r.ok ? r.json() : null));
          if (Array.isArray(uplistingData)) setUplistingConfigs(uplistingData);
        } catch { /* non-critical */ }
      }

      if (enableGuesty !== false) {
        try {
          const guestyData = await configCache.fetch("guesty", () =>
            api.admin.get("Guesty/Configs").then(r => r.ok ? r.json() : null));
          if (Array.isArray(guestyData)) setGuestyConfigs(guestyData);
        } catch { /* non-critical */ }
      }

      if (enableCloudbeds !== false) {
        try {
          const cloudbedsData = await configCache.fetch("cloudbeds", () =>
            api.admin.get("Cloudbeds/Configs").then(r => r.ok ? r.json() : null));
          if (Array.isArray(cloudbedsData)) setCloudbedsConfigs(cloudbedsData);
        } catch { /* non-critical */ }
      }

      if (enablePipedrive !== false) {
        try {
          const pipedriveData = await configCache.fetch("pipedrive", () =>
            api.admin.get("Pipedrive/Configs").then(r => r.ok ? r.json() : null));
          if (Array.isArray(pipedriveData)) setPipedriveConfigs(pipedriveData);
        } catch { /* non-critical */ }
      }

      if (enableQuickBooks !== false) {
        try {
          const qbData = await configCache.fetch("quickbooks", () =>
            api.admin.get("QuickBooks/Configs").then(r => r.ok ? r.json() : null));
          if (Array.isArray(qbData)) setQuickBooksConfigs(qbData);
        } catch { /* non-critical */ }
      }

      if (enableZoho !== false) {
        try {
          const zohoData = await configCache.fetch("zoho", () =>
            api.admin.get("Zoho/Configs").then(r => r.ok ? r.json() : null));
          if (Array.isArray(zohoData)) setZohoConfigs(zohoData);
        } catch { /* non-critical */ }
      }

      if (enableToast !== false) {
        try {
          const toastData = await configCache.fetch("toast", () =>
            api.admin.get("Toast/Configs").then(r => r.ok ? r.json() : null));
          if (Array.isArray(toastData)) setToastConfigs(toastData);
        } catch { /* non-critical */ }
      }

      if (enableClover !== false) {
        try {
          const cloverData = await configCache.fetch("clover", () =>
            api.admin.get("Clover/Configs").then(r => r.ok ? r.json() : null));
          if (Array.isArray(cloverData)) setCloverConfigs(cloverData);
        } catch { /* non-critical */ }
      }

      if (enableMindbody !== false) {
        try {
          const mindbodyData = await configCache.fetch("mindbody", () =>
            api.admin.get("Mindbody/Configs").then(r => r.ok ? r.json() : null));
          if (Array.isArray(mindbodyData)) setMindbodyConfigs(mindbodyData);
        } catch { /* non-critical */ }
      }

      if (enableJobber !== false) {
        try {
          const jobberData = await configCache.fetch("jobber", () =>
            api.admin.get("Jobber/Configs").then(r => r.ok ? r.json() : null));
          if (Array.isArray(jobberData)) setJobberConfigs(jobberData);
        } catch { /* non-critical */ }
      }

      try {
        const otData = await configCache.fetch("opentable", () =>
          api.admin.get("OpenTable/Config").then(r => r.ok ? r.json() : null));
        if (otData) setOpenTableConfig(otData);
      } catch { /* non-critical */ }

      try {
        const resyData = await configCache.fetch("resy", () =>
          api.admin.get("Resy/Config").then(r => r.ok ? r.json() : null));
        if (resyData) setResyConfig(resyData);
      } catch { /* non-critical */ }
    }

    loadConfigs();

    // Load platform detection suggestions (non-blocking)
    api.admin.get("SiteDetection/Suggestions").then(async (res) => {
      if (res.ok) {
        const data = await res.json();
        setSuggestions(Array.isArray(data.suggestions) ? data.suggestions : []);
        setSuggestionsDismissed(!!data.dismissed);
      }
    }).catch(() => {/* non-critical */});
  }, [api, teamId, enableFormstack, enableHubSpot, enableBigCommerce, enableDynamics, enableMagento]);

  const handleModalClose = () => {
    setShowIntegrationsModal(false);
  };

  const handleDismissSuggestions = async () => {
    setSuggestionsDismissed(true);
    try { await api.admin.post("SiteDetection/Suggestions/Dismiss", {}); } catch { /* non-critical */ }
  };

  const modifiedCategories = appCategories.map((cat) => ({
    ...cat,
    label: cat.value === selectedCategory ? ` ${cat.label}` : cat.label
  }));

  const activeSuggestions = suggestions.filter(s => !s.dismissed && !suggestionsDismissed);

  return (
    <div className="panel bg-white dark:bg-slate-800 rounded-2xl shadow-md mb-4 p-4 xl:max-w-[1350px] xl:justify-center xl:mx-auto">

      {/* ── Detection Suggestions Banner ─────────────────────────────────── */}
      {activeSuggestions.length > 0 && (
        <div className="mb-5 border border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800 rounded-xl p-4">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                💡 We detected tools on your site — want to connect them?
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                Based on your widget activity. Connect these integrations to unlock AI-powered features.
              </p>
            </div>
            <button
              onClick={handleDismissSuggestions}
              className="text-blue-400 hover:text-blue-600 text-lg leading-none flex-shrink-0"
              aria-label="Dismiss suggestions"
            >×</button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {activeSuggestions.map((s) => (
              <button
                key={s.integrationId + s.platform}
                onClick={() => navigate(s.integrationUrl)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded-lg text-sm text-blue-800 dark:text-blue-200 hover:bg-blue-50 dark:hover:bg-blue-800 transition-colors"
                title={s.reason}
              >
                <span className="font-medium">{s.integrationName}</span>
                <span className="text-blue-400 dark:text-blue-500">→</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div className="text-center text-slate-800 text-lg font-medium font-['Rubik'] leading-7">
          All Apps
          <span className="text-center text-slate-500 text-lg font-medium font-['Rubik'] leading-7 ml-2">
            {showApps.filter((app) => app.id !== "new").length} apps
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="search"
            placeholder="Search integrations…"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setVisibleCount(12); }}
            className="h-10 text-sm px-3 border border-slate-300 rounded-md w-52 focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
          <style>{`select option[value=""] { display: none; }`}</style>
          <Select
            className="w-48 h-12 text-sm px-3 py-2 border rounded-md !w-48"
            value={selectedCategory}
            options={modifiedCategories}
            onChange={(value) => { setSelectedCategory(value); setVisibleCount(12); }}
          />
        </div>
      </div>

      {!statusesReady ? (
        // Skeleton grid — fixed positions, prevents click-on-wrong-card during load
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-[220px]">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col gap-3 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-200 shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 bg-slate-200 rounded w-2/3" />
                  <div className="h-3 bg-slate-100 rounded w-1/2" />
                </div>
              </div>
              <div className="flex-1 space-y-2 mt-1">
                <div className="h-3 bg-slate-100 rounded w-full" />
                <div className="h-3 bg-slate-100 rounded w-5/6" />
                <div className="h-3 bg-slate-100 rounded w-4/6" />
              </div>
              <div className="h-7 bg-slate-100 rounded-lg w-24 mt-auto" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-[220px] netsuite-card">
          {showApps.slice(0, visibleCount).map((app) => (
            <Card
              key={app.id}
              to=""
              title={app.name}
              subtitle={app.subTitle}
              icon={app.icon}
              variant="integrations"
              isFirstCard={app.id === "new"}
              onClick={() => handleCardClick(app)}
              appStatus={String(appStatus[app.id] || "")}
              locked={isLocked(app.id)}
            />
          ))}
        </div>
      )}

      {statusesReady && visibleCount < showApps.length && (
        <div className="flex justify-center mt-4">
          <button
            onClick={handleLoadMore}
            className="text-blue-600 font-medium focus:outline-none"
          >
            Load More ({showApps.length - visibleCount} remaining)
          </button>
        </div>
      )}

      {showIntegrationsModal && selectedApp && (
        <IntegrationsModal
          show={showIntegrationsModal}
          onClose={handleModalClose}
          appId={selectedApp.id}
          appName={selectedApp.name}
          icon={selectedApp.icon || ""}
          teamId={teamId}
          appStatus={appStatus[selectedApp.id] || ""}
          configId={configId}
        />
      )}

      {/* ── New App request slide-in ─────────────────────────────────── */}
      <div
        className={`fixed top-0 right-0 h-full w-1/4 bg-white shadow-lg transition-transform duration-300 z-30 ${
          showNewAppPanel ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <IntegrationsNewApp onClose={() => setShowNewAppPanel(false)} />
      </div>

      {/* ── Unlock integration slide-in ───────────────────────────────── */}
      {/* Triggered when clicking a locked integration card.             */}
      {/* Shows marketplace upgrade paths AND direct Velaro contact.     */}
      <div
        className={`fixed top-0 right-0 h-full w-[420px] bg-white shadow-xl border-l border-slate-200 transition-transform duration-300 z-[60] flex flex-col ${
          upgradeTarget !== null ? "translate-x-0" : "translate-x-full"
        }`}
        role="dialog"
        aria-label="Unlock integration"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <h2 className="text-sm font-semibold text-slate-800">
            How to unlock {upgradeTarget?.appName ?? "this integration"}
          </h2>
          <button
            onClick={() => setUpgradeTarget(null)}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {upgradeTarget && (
            <UnlockIntegrationPanel
              key={upgradeTarget.appId}
              appId={upgradeTarget.appId}
              appName={upgradeTarget.appName}
              onClose={() => setUpgradeTarget(null)}
            />
          )}
        </div>
      </div>

      {/* Backdrop */}
      {upgradeTarget !== null && (
        <div
          className="fixed inset-0 bg-black/20 z-[59]"
          onClick={() => setUpgradeTarget(null)}
          aria-hidden="true"
        />
      )}
    </div>
  );
}
